import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type { KeyboardEvent } from "react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { VideoStatusSummary } from "@/features/marketplace/components/VideoStatusSummary";
import { DeadlineChip } from "@/features/marketplace/components/orders-list/DeadlineChip";
import { OrderTypeBadge } from "@/features/marketplace/components/orders-list/OrderTypeBadge";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import { StatusMenu } from "@/features/marketplace/components/orders-list/StatusMenu";
import {
  describeDeadline,
  localTodayYmd,
} from "@/features/marketplace/lib/order-deadline";
import { resolveOrderLineType } from "@/features/marketplace/lib/order-line-kind";
import { usesVideoStatusChips } from "@/features/marketplace/lib/line-status-display";
import {
  orderLineVideoKey,
  orderLineVideos,
  scriptHasExpandableVideos,
} from "@/features/marketplace/lib/order-line-videos";
import { describeOrderScript } from "@/features/marketplace/lib/order-script-display";
import { fulfillmentOptionsForOpsAdminScript } from "@/features/marketplace/lib/orders-list-model";
import { ORDERS_OVERDUE_ROW_CLASS } from "@/features/marketplace/lib/orders-overdue-row";
import type {
  AdminOrderRow,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";

import { AdminOrderVideoRow } from "./AdminOrderVideoRow";
import {
  ADMIN_ORDERS_CELL_ACTIONS_CLASS,
  ADMIN_ORDERS_CELL_DUE_CLASS,
  ADMIN_ORDERS_CELL_PARTNER_CLASS,
  ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
  ADMIN_ORDERS_CELL_SENT_CLASS,
  ADMIN_ORDERS_CELL_STATUS_CLASS,
  ADMIN_ORDERS_CELL_TYPE_CLASS,
  ADMIN_ORDERS_GRID_CLASS,
  ADMIN_ORDERS_ROW_PADDING_CLASS,
} from "./admin-orders-grid";

interface AdminOrderScriptRowProps {
  order: AdminOrderRow;
  script: OrderScriptRow;
  index: number;
  rowIndex: number;
  highlighted: boolean;
  selectedVideoKey: string | null;
  videosExpanded: boolean;
  onToggleVideosExpanded: () => void;
  onOpenVideo: (videoKey: string) => void;
  canEditStatus: boolean;
  pending: boolean;
  uniformDeadline: boolean;
  onOpen: () => void;
  onStatusChange: (next: MarketplaceFulfillmentStatus) => void;
}

export function AdminOrderScriptRow({
  order,
  script,
  index,
  rowIndex,
  highlighted,
  selectedVideoKey,
  videosExpanded,
  onToggleVideosExpanded,
  onOpenVideo,
  canEditStatus,
  pending,
  uniformDeadline,
  onOpen,
  onStatusChange,
}: AdminOrderScriptRowProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const todayYmd = localTodayYmd();
  const display = describeOrderScript(
    script,
    t("marketplace.orders.detail.unnamedScript", "Unnamed script"),
  );
  const status = script.fulfillmentStatus;
  const showRevisionNote =
    status === "SENT_FOR_REVISION" && Boolean(script.revisionNote?.trim());
  const fullText = [display.title, display.subtitle]
    .filter(Boolean)
    .join("\n\n");
  const deadline = describeDeadline(script.expectedDeliveryDate, todayYmd);
  const isOverdue =
    script.fulfillmentStatus !== "CANCELLED" &&
    deadline?.tone === "overdue";
  const expandableVideos = scriptHasExpandableVideos(script);
  const videos = orderLineVideos(script);
  const statusOptions = fulfillmentOptionsForOpsAdminScript(order, script);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onOpen();
    } else if (event.key === "ArrowRight" && expandableVideos && !videosExpanded) {
      event.preventDefault();
      onToggleVideosExpanded();
    } else if (event.key === "ArrowLeft" && expandableVideos && videosExpanded) {
      event.preventDefault();
      onToggleVideosExpanded();
    }
  };

  const legacyReview =
    !expandableVideos &&
    (script.scriptReviewId || script.preflightUrl) &&
    (status === "SUBMITTED_FOR_FEEDBACK" ||
      status === "SUBMITTED_FOR_FINAL_APPROVAL");

  return (
    <li role="presentation">
      <div
        role="row"
        aria-level={2}
        aria-rowindex={rowIndex}
        aria-expanded={expandableVideos ? videosExpanded : undefined}
        aria-controls={expandableVideos ? panelId : undefined}
        data-admin-script-row
        data-order-script-id={script.id}
        data-tree-row
        tabIndex={0}
        aria-label={t("marketplace.orders.list.scriptRowLabel", {
          index,
          title: display.title,
        })}
        aria-current={highlighted ? "true" : undefined}
        onClick={onOpen}
        onKeyDown={handleKeyDown}
        className={cn(
          ADMIN_ORDERS_GRID_CLASS,
          ADMIN_ORDERS_ROW_PADDING_CLASS,
          "group/script relative scroll-mt-20 min-h-12 cursor-pointer py-2 outline-none transition-colors",
          "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
          highlighted ? "bg-primary-subtle" : "bg-muted/20 hover:bg-muted/45",
          !highlighted && isOverdue && ORDERS_OVERDUE_ROW_CLASS,
        )}
      >
        <span aria-hidden role="gridcell" />
        <span role="gridcell" className="flex items-center">
          {expandableVideos ? (
            <button
              type="button"
              aria-expanded={videosExpanded}
              aria-label={t(
                videosExpanded
                  ? "marketplace.ordersDashboard.collapseVideos"
                  : "marketplace.ordersDashboard.expandVideos",
                {
                  title: display.title,
                  defaultValue: videosExpanded
                    ? "Collapse videos for {{title}}"
                    : "Expand videos for {{title}}",
                },
              )}
              className="flex size-7 -ml-1.5 cursor-pointer items-center justify-center rounded-sm text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
              onClick={(event) => {
                event.stopPropagation();
                onToggleVideosExpanded();
              }}
            >
              <Icon
                name="chevron-right"
                size="sm"
                color="inherit"
                className={cn(
                  "transition-transform duration-200 ease-out motion-reduce:transition-none",
                  videosExpanded && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          ) : null}
        </span>

        <span
          aria-hidden
          className="absolute bottom-0 left-[3.85rem] top-0 w-px bg-border"
        />
        <span
          aria-hidden
          className="absolute left-[3.85rem] top-1/2 h-px w-5 bg-border"
        />

        <div role="gridcell" className="min-w-0 pl-8">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="min-w-0">
                <p className="line-clamp-2 text-xs font-medium leading-4 text-foreground">
                  <span className="mr-1.5 tabular-nums text-muted-foreground/70">
                    {index}.
                  </span>
                  {display.title}
                </p>
                {display.subtitle ? (
                  <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-muted-foreground">
                    {display.subtitle}
                  </p>
                ) : null}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-md whitespace-pre-line">
              {fullText}
            </TooltipContent>
          </Tooltip>
          {showRevisionNote ? (
            <p className="mt-1 flex items-start gap-1 text-[11px] text-warning-subtle-foreground">
              <Icon
                name="message-circle"
                size="xs"
                color="inherit"
                className="mt-0.5 shrink-0"
                aria-hidden
              />
              <span className="min-w-0 line-clamp-1">
                <span className="font-medium">
                  {t("marketplace.orders.list.revisionNote")}:
                </span>{" "}
                {script.revisionNote}
              </span>
            </p>
          ) : null}
        </div>

        <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_PARTNER_CLASS} />
        <span role="gridcell" className={ADMIN_ORDERS_CELL_TYPE_CLASS}>
          <OrderTypeBadge type={resolveOrderLineType(script)} />
        </span>
        <span
          role="gridcell"
          aria-hidden
          className={cn(
            ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
            "text-xs text-muted-foreground/45",
          )}
        >
          —
        </span>

        <span role="gridcell" className={ADMIN_ORDERS_CELL_STATUS_CLASS}>
          {usesVideoStatusChips(script) ? (
            <VideoStatusSummary script={script} viewer="ops" />
          ) : (
            <StatusMenu
              value={status}
              options={statusOptions}
              editable={canEditStatus}
              pending={pending}
              ariaLabel={t("marketplace.orders.list.statusMenuLabel", { index })}
              onChange={onStatusChange}
            />
          )}
        </span>

        <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_SENT_CLASS} />

        <span role="gridcell" className={ADMIN_ORDERS_CELL_DUE_CLASS}>
          {uniformDeadline && deadline?.tone !== "overdue" ? (
            <span className="text-xs text-muted-foreground/45">—</span>
          ) : (
            <DeadlineChip
              ymd={script.expectedDeliveryDate}
              todayYmd={todayYmd}
              muted
            />
          )}
        </span>

        <span role="gridcell" className={ADMIN_ORDERS_CELL_ACTIONS_CLASS}>
          {legacyReview ? (
            <ScriptReviewAction
              scriptReviewId={script.scriptReviewId}
              preflightUrl={script.preflightUrl}
              label={t("marketplace.orders.list.review", "Review")}
              ariaLabel={t("marketplace.orders.list.reviewVideoAria", {
                title: display.title,
                defaultValue: "Review video for {{title}} (opens in a new tab)",
              })}
              variant="default"
              size="sm"
              compact
            />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-muted-foreground opacity-0 transition-opacity group-hover/script:opacity-100 group-focus-within/script:opacity-100 [@media(hover:none)]:opacity-100"
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
            >
              {t("marketplace.ordersDashboard.openScript", "Open")}
            </Button>
          )}
        </span>
      </div>

      {expandableVideos && videosExpanded ? (
        <ul id={panelId} role="presentation" className="flex flex-col">
          {videos.map((video, videoIndex) => {
            const videoKey = orderLineVideoKey(video);
            return (
              <AdminOrderVideoRow
                key={videoKey}
                orderId={order.id}
                orderScriptId={script.id}
                video={video}
                scriptTitle={display.title}
                rowIndex={rowIndex + videoIndex + 1}
                selected={highlighted && selectedVideoKey === videoKey}
                onSelect={() => {
                  onOpen();
                  onOpenVideo(videoKey);
                }}
              />
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}
